import type { ReactNode } from 'react';
import {
  CUSTOMER_EMAIL_FAILURE_FIX,
  CUSTOMER_EMAIL_FAILURE_LINE,
  CUSTOMER_EMAIL_STAFF_REJECTED_LINE,
  CUSTOMER_EMAIL_WAIT_LINE,
  DELIVERY_FAILURES,
  deliveryFix,
  deliveryProblem,
  knownNotSent,
  type CustomerEmailFailure,
  type CustomerEmailStatus,
  type CustomerEmailWait,
  type DeliveryFailure,
} from '@servwell/shared';
import { Notice } from './Badge';

/** A record's latest customer email, as its detail page reads it (MAP 249 spec §3). */
export interface CustomerEmailView {
  status: CustomerEmailStatus;
  waitingFor: CustomerEmailWait | null;
  failure: CustomerEmailFailure | null;
  /** The address it was written to, as it was then (not the customer's address today). */
  toEmail: string | null;
}

/**
 * How often a detail page asks again while its email is on its way: a clicked send is kicked
 * to the drain at once, so the line changes in seconds, not at the next reload (LESSONS
 * 2026-10-06: a status a background job changes needs the screen to ask again).
 */
export const CUSTOMER_EMAIL_POLL_MS = 5_000;

/** Is the email still going (queued and not waiting, or claimed by the drain)? */
export function customerEmailPending(
  email: CustomerEmailView | null | undefined,
): boolean {
  return (
    (email?.status === 'queued' && email.waitingFor === null) ||
    email?.status === 'sending'
  );
}

/**
 * Is the email still on its way, waiting included? Until it is not, Send stays off: a second
 * press would only answer this same email (the server returns it, Codex r1 #4), and a button
 * that looks pressable says otherwise.
 */
export function customerEmailInFlight(
  email: Pick<CustomerEmailView, 'status'> | null | undefined,
): boolean {
  return email?.status === 'queued' || email?.status === 'sending';
}

/**
 * The detail page's Send button for a record that emails its customer: off while the press is
 * out or the email is in flight, and named for what a press would do. "Send again" only once an
 * email has actually GONE (`sentAt`): after one that was skipped or failed, it is a first send.
 */
export function sendButtonState(input: {
  email: Pick<CustomerEmailView, 'status' | 'waitingFor'> | null;
  sentAt: Date | string | null;
  pressing: boolean;
}): { disabled: boolean; label: string } {
  if (input.pressing) return { disabled: true, label: 'Sending…' };
  if (customerEmailInFlight(input.email)) {
    return { disabled: true, label: input.email?.waitingFor ? 'Waiting…' : 'Sending…' };
  }
  return { disabled: false, label: input.sentAt ? 'Send again' : 'Email it to them' };
}

const isDeliveryFailure = (f: CustomerEmailFailure): f is DeliveryFailure =>
  (DELIVERY_FAILURES as readonly string[]).includes(f);

/**
 * What became of the shop's "Email it to them", from the outbox (MAP 249 spec §3): "Sending…",
 * "Sent", "Waiting: …" or "Didn't send: …".
 *
 * The reasons the shared delivery vocabulary has words for come from it (`deliveryProblem`,
 * `deliveryFix`: one vocabulary, `apps/web/test/delivery-copy.test.ts`); the outbox's own
 * (interrupted, unconfirmed, suppressed, superseded) from `CUSTOMER_EMAIL_FAILURE_LINE`. An
 * outcome that MAY have gone out never says "didn't send".
 */
export function CustomerEmailLine({
  email,
  children,
}: {
  email: CustomerEmailView;
  /** The surface's own sentence, on the did-not-go arm only. */
  children?: ReactNode;
}) {
  switch (email.status) {
    case 'queued':
      return email.waitingFor ? (
        <Notice tone="info">{CUSTOMER_EMAIL_WAIT_LINE[email.waitingFor]}.</Notice>
      ) : (
        <Notice tone="info">Sending…</Notice>
      );
    case 'sending':
      return <Notice tone="info">Sending…</Notice>;
    case 'sent':
      return <Notice tone="success">Sent to {email.toEmail ?? 'the customer'}.</Notice>;
    case 'skipped':
      // ServWell staff stopped it after a look (MAP 249 spec §4b): not the mail service's no.
      if (email.failure === 'rejected') {
        return (
          <Notice tone="warn">
            {CUSTOMER_EMAIL_STAFF_REJECTED_LINE}
            {children ? <> {children}</> : null}
          </Notice>
        );
      }
      return <NotSent failure={email.failure}>{children}</NotSent>;
    case 'failed':
      return <NotSent failure={email.failure}>{children}</NotSent>;
  }
}

function NotSent({
  failure,
  children,
}: {
  failure: CustomerEmailFailure | null;
  children?: ReactNode;
}) {
  const tail = children ? <> {children}</> : null;
  // The CHECK gives a skipped or failed row a reason; a row without one (a newer server's) still
  // says something true, and claims nothing.
  if (!failure) return <Notice tone="warn">Something stopped this email.{tail}</Notice>;
  const delivery = isDeliveryFailure(failure) ? failure : null;
  const outbox = delivery ? null : (failure as OutboxFailure);
  const words = delivery
    ? deliveryProblem(delivery)
    : CUSTOMER_EMAIL_FAILURE_LINE[outbox!];
  const fix = delivery ? deliveryFix(delivery) : CUSTOMER_EMAIL_FAILURE_FIX[outbox!];
  return (
    <Notice tone="warn">
      {/* ⚠️ "Didn't send" only on the arm that KNOWS: `knownNotSent` for the shared reasons, and
          the outbox's own (suppressed, superseded) are never sent; interrupted and unconfirmed
          may have gone. */}
      {(delivery ? knownNotSent(delivery) : !MAY_HAVE_GONE.includes(outbox!))
        ? `Didn’t send: ${words}. `
        : delivery
          ? `${words}, so this may or may not have reached them. `
          : `${words}. `}
      {fix}
      {tail}
    </Notice>
  );
}

type OutboxFailure = keyof typeof CUSTOMER_EMAIL_FAILURE_LINE;

/** The outbox's own failures that MAY have gone out: never "didn't send". */
const MAY_HAVE_GONE: readonly OutboxFailure[] = ['interrupted', 'unconfirmed'];
